/*
 * Brunel guarantee registration form helpers. The form works without this script; it only saves effort:
 *  - a search box narrows the product list as the customer types (by name or part number)
 *  - the guarantee that applies to the chosen tool is shown under the list
 *  - choosing one of the customer's own orders fills in the shop, the order number and the purchase date
 *  - the "product name" box only shows when "My tool isn't listed" is chosen
 *  - the "where did you buy it" box only shows when "Other" is chosen
 * Nothing is sent anywhere by this script. The form itself is Shopify's standard contact form.
 */
if (!customElements.get('brunel-guarantee-form')) {
  customElements.define(
    'brunel-guarantee-form',
    class BrunelGuaranteeForm extends HTMLElement {
      connectedCallback() {
        this.product = this.querySelector('select[data-product]');
        this.retailer = this.querySelector('select[data-retailer]');
        this.date = this.querySelector('input[data-date]');
        this.order = this.querySelector('input[data-order]');
        this.otherField = this.querySelector('[data-not-listed-field]');
        this.otherRetailerField = this.querySelector('[data-other-retailer-field]');
        this.panel = this.querySelector('[data-guarantee-panel]');
        this.guaranteeText = this.querySelector('[data-guarantee-text]');
        this.guaranteeNote = this.querySelector('[data-guarantee-note]');
        this.guaranteeInput = this.querySelector('[data-guarantee-input]');
        this.search = this.querySelector('input[data-search]');
        this.searchWrap = this.querySelector('[data-search-wrap]');
        this.searchStatus = this.querySelector('[data-search-status]');
        if (!this.product) return;

        this.onProductChange = this.onProductChange.bind(this);
        this.onRetailerChange = this.onRetailerChange.bind(this);
        this.onSearch = this.onSearch.bind(this);
        this.onSearchKeydown = this.onSearchKeydown.bind(this);

        this.product.addEventListener('change', this.onProductChange);
        this.retailer?.addEventListener('change', this.onRetailerChange);

        if (this.search && this.searchWrap) {
          // Keep a copy of the full list so it can be narrowed and restored.
          this.fullList = [...this.product.children].map((node) => node.cloneNode(true));
          this.searchWrap.hidden = false;
          this.search.addEventListener('input', this.onSearch);
          this.search.addEventListener('keydown', this.onSearchKeydown);
        }

        // These boxes are now only shown when they apply, so their "only if..." hints are not needed.
        [this.otherField, this.otherRetailerField].forEach((field) => field?.querySelector('.bru-form__optional')?.remove());

        this.onProductChange();
        this.onRetailerChange();
      }

      disconnectedCallback() {
        this.product?.removeEventListener('change', this.onProductChange);
        this.retailer?.removeEventListener('change', this.onRetailerChange);
        this.search?.removeEventListener('input', this.onSearch);
        this.search?.removeEventListener('keydown', this.onSearchKeydown);
      }

      // Enter in the search box must not submit a half-filled form.
      onSearchKeydown(event) {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.product.focus();
        }
      }

      onSearch() {
        const query = this.search.value.trim();
        const words = query.toLowerCase().split(/\s+/).filter(Boolean);
        const previous = this.product.value;
        const list = document.createDocumentFragment();
        const found = [];

        const matches = (option) => {
          const text = (option.dataset.search || option.textContent).toLowerCase();
          return words.every((word) => text.includes(word));
        };

        this.fullList.forEach((node) => {
          if (node.tagName !== 'OPTGROUP') {
            // The prompt and "My tool isn't listed" always stay in the list.
            list.appendChild(node.cloneNode(true));
            return;
          }
          const group = node.cloneNode(false);
          [...node.children].forEach((option) => {
            if (words.length && !matches(option)) return;
            group.appendChild(option.cloneNode(true));
            found.push(option.value);
          });
          if (group.children.length) list.appendChild(group);
        });

        this.product.replaceChildren(list);

        const options = [...this.product.options];
        if (previous && options.some((option) => option.value === previous)) {
          this.product.value = previous;
        } else if (words.length && found.length === 1) {
          this.product.value = found[0];
        } else {
          this.product.selectedIndex = 0;
        }

        if (this.searchStatus) {
          let message = '';
          if (words.length && found.length === 0) {
            const notListed = this.product.querySelector('option[data-not-listed]');
            const fallback = notListed ? ` or choose "${notListed.textContent.trim()}"` : '';
            message = `Nothing matches "${query}". Check the spelling${fallback}.`;
          } else if (words.length && found.length === 1) {
            message = '1 match, selected for you.';
          } else if (words.length) {
            message = `${found.length} matches. Pick yours from the list.`;
          }
          this.searchStatus.textContent = message;
          this.searchStatus.hidden = !message;
        }

        this.onProductChange();
      }

      // Shows the guarantee that applies to the chosen tool, and sends the same wording with the form.
      showGuarantee(option) {
        const text = option && option.value ? option.dataset.guarantee || '' : '';
        const note = text ? option.dataset.note || '' : '';
        if (this.guaranteeText) this.guaranteeText.textContent = text;
        if (this.guaranteeNote) {
          this.guaranteeNote.textContent = note;
          this.guaranteeNote.hidden = !note;
        }
        if (this.panel) this.panel.hidden = !text;
        if (this.guaranteeInput) this.guaranteeInput.value = note ? `${text}. ${note}` : text;
      }

      // Shows a text box only while it is needed, and makes it required for as long as it is shown.
      toggleExtraField(field, show) {
        if (!field) return;
        field.hidden = !show;
        const input = field.querySelector('input');
        if (!input) return;
        input.required = show;
        if (!show) input.value = '';
      }

      onRetailerChange() {
        if (!this.retailer) return;
        const option = this.retailer.selectedOptions[0];
        this.toggleExtraField(this.otherRetailerField, Boolean(option && option.hasAttribute('data-other')));
      }

      onProductChange() {
        const option = this.product.selectedOptions[0];
        this.toggleExtraField(this.otherField, Boolean(option && option.hasAttribute('data-not-listed')));
        this.showGuarantee(option);

        if (!option || !option.dataset.order) return;

        // One of the customer's own orders: we already know where and when it was bought.
        if (this.order) this.order.value = option.dataset.order;
        if (this.date && option.dataset.date) this.date.value = option.dataset.date;
        if (this.retailer) {
          const thisShop = this.retailer.querySelector('option[data-this-shop]');
          if (thisShop) {
            this.retailer.value = thisShop.value;
            this.onRetailerChange();
          }
        }
      }
    }
  );
}
